import './QuizResults.css';
import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { saveQuizResult } from '../../services/quizResultsService';

// Pure function — single source of truth for answer correctness.
// Used both when saving the result and when rendering the review.
const isAnswerCorrect = (question, answer) => {
    if (answer === undefined || answer === null) return false;
    switch (question.type) {
        case 'Multichoice':
            return answer === question.correctAnswer;
        case 'MultipleCorrect': {
            if (!Array.isArray(answer) || answer.length === 0) return false;
            const sa = [...answer].sort();
            const sc = [...question.correctAnswers].sort();
            return sa.length === sc.length && sa.every((v, i) => v === sc[i]);
        }
        case 'FillIn': {
            if (typeof answer !== 'string') return false;
            const ua = answer.trim().toLowerCase();
            const accepted = question.correctAnswers || (question.correctAnswer ? [question.correctAnswer] : []);
            return accepted.some(a => a.toLowerCase() === ua);
        }
        case 'Linking': {
            if (!Array.isArray(answer) || answer.length !== question.correctPairs.length) return false;
            return answer.every(p => question.correctPairs.some(c => c.left === p.left && c.right === p.right));
        }
        case 'Timeline': {
            if (!Array.isArray(answer) || answer.length !== question.correctOrder.length) return false;
            return answer.every((v, i) => v === question.correctOrder[i]);
        }
        case 'FindInText': {
            if (!Array.isArray(answer) || answer.length === 0) return false;
            const su = [...answer].sort((a, b) => a - b);
            const sc = [...question.correctIndices].sort((a, b) => a - b);
            return su.length === sc.length && su.every((v, i) => v === sc[i]);
        }
        default:
            return false;
    }
};

function QuizResults() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const selectedAnswers = location.state?.selectedAnswers || {};
    const questions = location.state?.questions || [];
    const returnPath = location.state?.returnPath;

    const [expandedQuestions, setExpandedQuestions] = useState({});
    const [saveError, setSaveError] = useState(null);

    // Save result on mount for real quizzes (not reading-list virtual quiz)
    useEffect(() => {
        if (!id || id === 'muj-seznam' || !questions || questions.length === 0) return;
        const correctCount = questions.filter(q => isAnswerCorrect(q, selectedAnswers[q.id])).length;
        saveQuizResult(id, correctCount, questions.length)
            .catch(() => setSaveError('Výsledek kvízu se nepodařilo uložit. Zkontrolujte připojení.'));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    if (!questions || questions.length === 0) {
        return (
            <div className="quiz-results-page">
                <div className="quiz-results-container">
                    <div className="not-found">
                        <h1>Výsledky nejsou k dispozici</h1>
                        <p>Dokončete kvíz nejprve nebo se vraťte na jeho detail.</p>
                        <button onClick={() => navigate(`/kvizy/${id}`)} className="back-button">
                            Zpět na detail kvízu
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const getFirstTenWords = (text) => {
        const words = (text || '').split(/\s+/);
        return words.slice(0, 10).join(' ') + (words.length > 10 ? '...' : '');
    };

    const stripPunctuation = (word) => {
        if (!word) return '';
        return word.replace(/[.,!?;:"""''„"«»()[\]{}]/g, '');
    };

    const toggleQuestion = (questionId) => {
        setExpandedQuestions(prev => ({
            ...prev,
            [questionId]: !prev[questionId]
        }));
    };

    const getUserAnswerDisplay = (question) => {
        const answer = selectedAnswers[question.id];

        if (!answer && answer !== 0) {
            return <span className="answer-missing">Nezodpovězeno</span>;
        }

        switch (question.type) {
            case 'Multichoice': {
                const choice = question.choices.find(c => c.id === answer);
                return choice?.text || 'N/A';
            }
            case 'MultipleCorrect': {
                if (!Array.isArray(answer) || answer.length === 0) {
                    return <span className="answer-missing">Žádná odpověď</span>;
                }
                const selectedChoices = question.choices
                    .filter(c => answer.includes(c.id))
                    .map(c => c.text);
                return selectedChoices.join(', ');
            }
            case 'FillIn':
                return answer;
            case 'Linking': {
                if (!Array.isArray(answer) || answer.length === 0) {
                    return <span className="answer-missing">Žádné páry</span>;
                }
                const pairTexts = answer.map(pair => {
                    const leftItem = question.leftItems.find(item => item.id === pair.left);
                    const rightItem = question.rightItems.find(item => item.id === pair.right);
                    return `(${leftItem?.text || 'N/A'}, ${rightItem?.text || 'N/A'})`;
                });
                return pairTexts.join(', ');
            }
            case 'Timeline': {
                if (!Array.isArray(answer) || answer.length === 0) {
                    return <span className="answer-missing">Neseřazeno</span>;
                }
                const orderedItems = answer.map((itemId, index) => {
                    const item = question.items.find(i => i.id === itemId);
                    return `${index + 1}. ${item?.text || 'N/A'}`;
                });
                return orderedItems.join(', ');
            }
            case 'FindInText': {
                if (!Array.isArray(answer) || answer.length === 0) {
                    return <span className="answer-missing">Nevybráno</span>;
                }
                const userTokens = (question.text || '').split(/\s+/);
                const userSelectedWords = [...answer].sort((a, b) => a - b)
                    .map(i => userTokens[i])
                    .filter(Boolean)
                    .map(w => stripPunctuation(w));
                return userSelectedWords.join(', ') || <span className="answer-missing">Nevybráno</span>;
            }
            default:
                return 'N/A';
        }
    };

    const getCorrectAnswerDisplay = (question) => {
        switch (question.type) {
            case 'Multichoice': {
                const correctChoice = question.choices.find(c => c.id === question.correctAnswer);
                return correctChoice?.text || 'N/A';
            }
            case 'MultipleCorrect': {
                const correctChoices = question.choices
                    .filter(c => question.correctAnswers.includes(c.id))
                    .map(c => c.text);
                return correctChoices.join(', ');
            }
            case 'FillIn':
                return (question.correctAnswers || []).join(', ') || question.correctAnswer || 'N/A';
            case 'Linking': {
                const correctPairTexts = question.correctPairs.map(pair => {
                    const leftItem = question.leftItems.find(item => item.id === pair.left);
                    const rightItem = question.rightItems.find(item => item.id === pair.right);
                    return `(${leftItem?.text || 'N/A'}, ${rightItem?.text || 'N/A'})`;
                });
                return correctPairTexts.join(', ');
            }
            case 'Timeline': {
                const correctOrderItems = question.correctOrder.map((itemId, index) => {
                    const item = question.items.find(i => i.id === itemId);
                    return `${index + 1}. ${item?.text || 'N/A'}`;
                });
                return correctOrderItems.join(', ');
            }
            case 'FindInText': {
                const correctTokens = (question.text || '').split(/\s+/);
                const correctWords = question.correctIndices
                    .sort((a, b) => a - b)
                    .map(i => correctTokens[i])
                    .filter(Boolean)
                    .map(w => stripPunctuation(w));
                return correctWords.join(', ') || 'N/A';
            }
            default:
                return 'N/A';
        }
    };

    const results = questions.map((question, index) => ({
        question,
        index,
        isCorrect: isAnswerCorrect(question, selectedAnswers[question.id]),
        previewText: getFirstTenWords(question.questionText)
    }));

    const correctCount = results.filter(r => r.isCorrect).length;
    const totalQuestions = questions.length;
    const percentage = Math.round((correctCount / totalQuestions) * 100);

    return (
        <div className="quiz-results-page">
            <div className="quiz-results-container">
                {/* Header with Quiz Title */}
                <div className="results-header">
                    <h1 className="quiz-title">Výsledky kvízu</h1>
                </div>

                {/* Save error notice */}
                {saveError && (
                    <div className="list-sync-error" style={{ marginBottom: '1rem' }}>
                        {saveError}
                    </div>
                )}

                {/* Score Section */}
                <div className="score-section">
                    <div className="score-display">
                        <span className="score-number">{correctCount}</span>
                        <span className="score-divider">/</span>
                        <span className="score-total">{totalQuestions}</span>
                    </div>
                    <div className="score-label">správných odpovědí ({percentage}%)</div>

                    {/* Progress Bar */}
                    <div className="results-progress-bar">
                        <div
                            className="results-progress-fill"
                            style={{ width: `${percentage}%` }}
                        ></div>
                    </div>
                </div>

                {/* Questions List */}
                <div className="results-questions-list">
                    {results.map((result) => {
                        const isExpanded = expandedQuestions[result.question.id];

                        return (
                            <div
                                key={result.question.id}
                                className={`result-question-tile ${result.isCorrect ? 'correct' : 'incorrect'} ${isExpanded ? 'expanded' : ''}`}
                            >
                                <div
                                    className="result-question-header"
                                    onClick={() => toggleQuestion(result.question.id)}
                                >
                                    <div className="result-indicator">
                                        {result.isCorrect ? '✓' : '✗'}
                                    </div>
                                    <div className="result-question-preview">
                                        <span className="question-preview-number">#{result.index + 1}</span>
                                        <span className="question-preview-text">{result.previewText}</span>
                                    </div>
                                    <div className="expand-arrow">
                                        {isExpanded ? '▼' : '▶'}
                                    </div>
                                </div>

                                {isExpanded && (
                                    <div className="result-question-details">
                                        <div className="detail-section">
                                            <div className="detail-label">Otázka:</div>
                                            <div className="detail-text">{result.question.questionText}</div>
                                            {result.question.type === 'FillIn' && result.question.text && (() => {
                                                const words = result.question.text.trim().split(/\s+/);
                                                const blankPos = result.question.blankPosition ?? null;
                                                const blankCount = result.question.correctAnswers?.length ?? 1;
                                                if (blankPos === null) return null;
                                                const before = words.slice(0, blankPos).join(' ');
                                                const blanked = words.slice(blankPos, blankPos + blankCount).join(' ');
                                                const after = words.slice(blankPos + blankCount).join(' ');
                                                return (
                                                    <div className="detail-fillin-text">
                                                        {before && <span>{before} </span>}
                                                        <span className="detail-fillin-blank">{blanked}</span>
                                                        {after && <span> {after}</span>}
                                                    </div>
                                                );
                                            })()}
                                        </div>

                                        <div className="detail-section">
                                            <div className="detail-label">Vaše odpověď:</div>
                                            <div className={`detail-answer ${result.isCorrect ? 'answer-correct' : 'answer-incorrect'}`}>
                                                {getUserAnswerDisplay(result.question)}
                                            </div>
                                            {(result.question.type === 'Multichoice' || result.question.type === 'MultipleCorrect') && (() => {
                                                const answer = selectedAnswers[result.question.id];
                                                const answeredIds = result.question.type === 'Multichoice' ? [answer] : (Array.isArray(answer) ? answer : []);
                                                const notes = result.question.choices
                                                    ?.filter(c => answeredIds.includes(c.id) && c.note)
                                                    .map(c => c.note);
                                                return notes?.length > 0
                                                    ? <div className="detail-note">{notes.join(' ')}</div>
                                                    : null;
                                            })()}
                                        </div>

                                        {!result.isCorrect && (
                                            <div className="detail-section">
                                                <div className="detail-label">Správná odpověď:</div>
                                                <div className="detail-answer answer-correct">
                                                    {getCorrectAnswerDisplay(result.question)}
                                                </div>
                                                {(result.question.type === 'Multichoice' || result.question.type === 'MultipleCorrect') && (() => {
                                                    const correctIds = result.question.type === 'Multichoice'
                                                        ? [result.question.correctAnswer]
                                                        : (result.question.correctAnswers || []);
                                                    const notes = result.question.choices
                                                        ?.filter(c => correctIds.includes(c.id) && c.note)
                                                        .map(c => c.note);
                                                    return notes?.length > 0
                                                        ? <div className="detail-note">{notes.join(' ')}</div>
                                                        : null;
                                                })()}
                                            </div>
                                        )}

                                        {(result.question.relatedBook || result.question.relatedAuthorId) && (
                                            <div className="detail-section">
                                                <div className="detail-label">Související obsah:</div>
                                                <div className="related-content-links">
                                                    {result.question.relatedBook && (
                                                        <a
                                                            href={`/knihy/${result.question.relatedBook}`}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="related-content-link"
                                                        >
                                                            {result.question.relatedBookTitle || 'Detail knihy'}
                                                        </a>
                                                    )}
                                                    {result.question.relatedAuthorId && (
                                                        <a
                                                            href={`/autori/${result.question.relatedAuthorId}`}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="related-content-link"
                                                        >
                                                            {result.question.relatedAuthorName || 'Detail autora'}
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Action Buttons */}
                <div className="results-actions">
                    <button
                        onClick={() => navigate(`/kvizy/${id}/summary`, { state: { selectedAnswers, questions, returnPath } })}
                        className="nav-button back-to-summary-button"
                    >
                        ← Zpět na shrnutí
                    </button>
                    <button
                        onClick={() => navigate(returnPath || `/kvizy/${id}`)}
                        className="nav-button finish-button"
                    >
                        Ukončit kvíz
                    </button>
                </div>
            </div>
        </div>
    );
}

export default QuizResults;
